import { useMemo } from "react";
import { useThemeSlice } from "../../components/ui/StatusBadge.jsx";
import { EntityLoadingState } from "../../components/ui/EntityPageChrome.jsx";
import { compactPrimaryButton } from "../../components/ui/buttonStyles.js";
import {
  PageErrorBanner,
  TypeCatalogCard,
  TypeCatalogListFooter,
  TypeCatalogSectionHeader,
  TypeCatalogToolbar,
} from "../../components/ui/TypeCatalogToolbar.jsx";
import { TypeCatalogEmptyState } from "../../components/emptyStates/TypeCatalogEmptyState.jsx";
import { DeleteConfirmModal } from "../../components/modal/DeleteConfirmModal.jsx";
import { Ico } from "../../components/icons/icons.jsx";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm.js";
import { useSimTypes } from "../../logic/hooks/useSimTypes.js";
import { buildAccountDetailTokens } from "../Accounts/accountDetailTheme.js";
import { SimTypeList } from "./components/SimTypeList.jsx";
import { SimTypeModal } from "./components/SimTypeModal.jsx";

export function SimTypesPage() {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildAccountDetailTokens(dark, c, A), [dark, c, A]);
  const vm = useSimTypes();
  const { requestDelete, deleteModalProps } = useDeleteConfirm();

  const showing = vm.items.length;
  const total = vm.allItems.length;
  const filtered = vm.search.trim().length > 0;
  const countLabel = filtered ? `${showing} of ${total} types` : `${total} type${total === 1 ? "" : "s"}`;

  const handleDelete = (row) => {
    requestDelete({
      title: "Delete SIM type",
      description: "This removes the plan definition. Existing SIMs using this type are not affected in this demo.",
      confirmText: row.name,
      entityLabel: "SIM type",
      onConfirm: () => vm.removeSimType(row.id),
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden", padding: "14px" }}>
      <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
        <TypeCatalogSectionHeader
          title="SIM types"
          subtitle="Define SIM plan categories — each type specifies data plan, roaming, and custom configuration fields."
          action={
            <button type="button" onClick={vm.openCreate} style={compactPrimaryButton(A)}>
              {Ico.plus}
              Create SIM type
            </button>
          }
          c={c}
        />

        <PageErrorBanner message={vm.error} />

        <TypeCatalogCard ap={ap} dark={dark}>
          <TypeCatalogToolbar search={vm.search} onSearchChange={vm.setSearch} placeholder="Search SIM types…" countLabel={countLabel} c={c} />

          {vm.loading ? (
            <EntityLoadingState />
          ) : total === 0 ? (
            <TypeCatalogEmptyState c={c} A={A} icon={Ico.sim} message="No SIM types yet" hint="Create a type to define plans like M2M or Roaming." onAction={vm.openCreate} actionLabel="Create SIM type" />
          ) : showing === 0 ? (
            <TypeCatalogEmptyState c={c} A={A} icon={Ico.sim} message="No matching SIM types" hint="Try a different search term." />
          ) : (
            <SimTypeList items={vm.items} ap={ap} onEdit={vm.openEdit} onDelete={handleDelete} />
          )}

          {!vm.loading && showing > 0 && (
            <TypeCatalogListFooter c={c}>
              Showing {showing} SIM type{showing === 1 ? "" : "s"}
              {filtered ? ` matching “${vm.search.trim()}”` : ""}
            </TypeCatalogListFooter>
          )}
        </TypeCatalogCard>

        <SimTypeModal open={vm.modalOpen} onClose={vm.closeModal} onSave={vm.saveSimType} editing={vm.editing} saving={vm.saving} />
        <DeleteConfirmModal {...deleteModalProps} />
      </div>
    </div>
  );
}
